import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { EnvironmentActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';

/**
 * Any navigation to a lazy-loaded route triggers a webpack chunk load. On a lost/degraded connection
 * (or against a stale bundle after a deploy whose chunk hashes were purged) that load fails with a
 * `ChunkLoadError` at the router's recognition phase — BEFORE the target route's guards or the
 * translation flow ever run — so the navigation dies silently with no user feedback. This handler
 * listens for the router's NavigationError and, when it is a chunk-load failure, dispatches
 * ChunkLoadFailure so a browser-only effect can surface the generic offline modal. It is NOT
 * language-specific: any lazy route can fail this way, so no language is attributed here.
 *
 * Scope: we hook router.events ONLY, not the global ErrorHandler, to avoid a double-fire (a single
 * failed navigation would otherwise surface through both). A non-router dynamic import that fails
 * (e.g. the video player's `ngx-plyr-mg` lazy import) surfaces through the ErrorHandler and is out
 * of scope here. Browser-only (SSR never lazy-loads a route chunk over the network).
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadErrorHandler {
    constructor(
        private router: Router,
        private store$: Store<fromRoot.State>,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.router.events
            .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
            .subscribe((event) => {
                if (this.isChunkLoadError(event.error)) {
                    // Pass the failed navigation URL: when it is a language route, a reconnect can
                    // re-navigate there to complete the switch (the reducer filters non-language URLs).
                    this.store$.dispatch(EnvironmentActions.ChunkLoadFailure({ url: event.url }));
                }
            });
    }

    private isChunkLoadError(error: unknown): boolean {
        if (!error) {
            return false;
        }
        // `error` is `unknown` (NavigationError.error). We only read two OPTIONAL diagnostic fields
        // defensively, each with a fallback, so a narrowing type guard would add noise without changing
        // behaviour: a non-object error yields `undefined` name and stringifies for the message probe.
        const name = (error as { name?: string }).name;
        const message = String((error as { message?: string }).message ?? error);
        // Match ONLY the two reliable chunk-load signatures. A bare /failed to fetch/i is deliberately
        // NOT matched: it also covers guard/resolver `fetch()` TypeErrors across ~30 routes, which are
        // not chunk failures and must not open this modal.
        return name === 'ChunkLoadError' || /loading chunk .* failed/i.test(message);
    }
}
